import { useEffect, useState } from "react";
import api from "@/lib/api";
import { openAuthedFile, isImage } from "@/lib/files";
import { FileText, Image as ImageIcon, Eye, AlertTriangle, CheckCircle2, FolderOpen } from "lucide-react";

const isExpired = (d) => d && new Date(d) < new Date(new Date().toDateString());

const LABELS = {
  licence: "Licence (PPL/LAPL)", medical: "Certificat médical",
  manuel_vol: "Manuel de vol", fiche_pesee: "Fiche de pesée",
  immatriculation: "Certificat d'immatriculation", acoustique: "Certificat acoustique",
  cdn: "CDN / CEN", licence_station: "Licence de station d'aéronef", assurance: "Assurance",
};

// Read-only view of documents linked from the Pilote / Avion profile.
export default function LinkedDocuments({ entityType, entityId, title, emptyLabel, manageHref }) {
  const [docs, setDocs] = useState([]);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    if (!entityId) { setLoaded(true); return; }
    api.get(`/${entityType}/${entityId}/documents`)
      .then((r) => setDocs(r.data))
      .catch(() => setDocs([]))
      .finally(() => setLoaded(true));
  }, [entityType, entityId]);

  return (
    <div className="rounded-xl border border-cyan-500/20 bg-slate-900/30 p-4" data-testid={`linked-docs-${entityType}`}>
      <div className="mb-3 flex items-center justify-between">
        <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-cyan-400">
          <FolderOpen className="h-3.5 w-3.5" /> {title}
        </div>
        {manageHref && (
          <a href={manageHref} target="_blank" rel="noopener noreferrer" className="text-[11px] text-cyan-400 hover:text-cyan-300">
            Gérer la fiche
          </a>
        )}
      </div>

      {!loaded ? (
        <p className="text-xs text-slate-500">Chargement…</p>
      ) : !entityId ? (
        <p className="text-xs text-slate-500">{emptyLabel}</p>
      ) : docs.length === 0 ? (
        <p className="text-xs text-slate-500">Aucun document dans la fiche. Ajoutez-les ci-dessous ou dans la fiche.</p>
      ) : (
        <ul className="space-y-2">
          {docs.map((d) => {
            const expired = isExpired(d.validity_date);
            return (
              <li key={d.id} data-testid={`linked-doc-${d.id}`}
                className="flex items-center gap-2 rounded-lg border border-slate-800 bg-slate-950/40 px-3 py-2">
                {isImage(d.content_type) ? <ImageIcon className="h-4 w-4 text-cyan-500/70" /> : <FileText className="h-4 w-4 text-cyan-500/70" />}
                <span className="flex-1 truncate text-sm text-slate-200">{LABELS[d.doc_type] || d.label || d.doc_type}</span>
                {d.validity_date && (
                  <span className={`flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-semibold ${expired ? "bg-rose-500/10 text-rose-300" : "bg-emerald-500/10 text-emerald-300"}`}>
                    {expired ? <AlertTriangle className="h-3 w-3" /> : <CheckCircle2 className="h-3 w-3" />}
                    {expired ? "Expiré" : "Valide"} · {d.validity_date}
                  </span>
                )}
                <button onClick={() => openAuthedFile(`/${entityType}/${entityId}/documents/${d.id}/download`)}
                  className="rounded p-1.5 text-slate-400 hover:bg-slate-800 hover:text-cyan-300" title="Voir">
                  <Eye className="h-4 w-4" />
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
